<!-- Макет: https://www.figma.com/file/hZ14djvbx5qYuIJwSU7moW/%D0%AD%D0%BB%D0%B5%D0%BC%D0%B5%D0%BD%D1%82%D1%8B-%F0%9F%94%A5?node-id=6851%3A140000 -->
<template>
  <div class="goods-item">
    <div
      v-if="goods.image"
      class="goods-item__image-container"
    >
      <BHLink
        v-if="goodsLink"
        :href="goodsLink"
        target="_blank"
      >
        <BasePicture
          ref="image"
          class="goods-item__picture"
          :alt="image?.alt || ''"
          :sizes="sizes"
          :origin="fullSizeUrl"
          @load="onImgLoad"
        />
      </BHLink>
      <BasePicture
        v-else
        ref="image"
        class="goods-item__picture"
        :fullsize="fullSizeUrl"
        :sizes="sizes"
        @load="onImgLoad"
      />
    </div>

    <div
      ref="content"
      class="goods-item__content"
      :style="styleContent"
    >
      <div
        v-if="title"
        class="goods-item__title"
      >
        {{ title }}
      </div>
      <div
        v-if="price"
        class="goods-item__price"
      >
        {{ price }}
      </div>
      <div
        v-if="goodsContent.length"
        ref="description"
        class="goods-item__description"
      >
        <div
          ref="descriptionContent"
          class="goods-item__description-content"
          :class="descriptionContentClass"
          :style="descriptionContentStyle"
        >
          <TheContent
            v-for="content in goodsContent"
            :key="content.id"
            :elem="content"
          />
        </div>
        <div
          v-if="isDescriptionCollapsed"
          class="goods-item__description-shadow"
        />
      </div>

      <button
        v-if="isDescriptionCollapsed && goodsContent.length"
        class="goods-item__description-collapse"
        @click="onClickMore"
      >
        Показать ещё
      </button>

      <BHLink
        v-if="isGoodsButton"
        class="goods-item__button"
        :href="goodsLink"
        target="_blank"
        @click="onClickBuy()"
      >
        {{ goods.button_text }}
      </BHLink>
    </div>
  </div>
</template>

<script lang="ts">
  import type { PropType } from 'vue';
  import { isMobile } from 'mobile-device-detect';
  import type { IContentGoods, TGoods } from '~/types/components/GoodsItem';
  import type { TContent, TImage, TImageSize } from '~/types/common';
  import TheContent from '~/components/TheContent';
  import getSlugFromUrl from '~/utils/getSlugFromUrl';
  import BHLink from '~/components/BHLink.vue';
  import BasePicture from '@devhacker/shared/features/BasePicture/BasePicture.vue';

  export default defineNuxtComponent({
    name: 'GoodsItem',

    components: { BasePicture, BHLink, TheContent },

    provide() {
      return {
        eventCategory: () => 'Внутренняя',
        eventAction: () => 'Товарный блок_Внешний переход',
      };
    },

    inject: {
      // Категория в которой происходит событие
      // Для аналитики
      eventCategory: {
        default() {
          return () => '';
        },
      },
    },

    props: {
      elem: {
        type: Object as PropType<IContentGoods>,
        required: true,
      },
    },

    data: () => ({
      isDescriptionCollapsed: false as boolean,
      styleContent: null as Record<string, any> | null,
      // высота 160 соответствует 8 строкам содержимого
      // css line-clamp не получается использовать
      // т.к. в safari не перносится это св-во
      // на строки в дочерних компонентах
      descriptionHeightMobile: 160 as number,
      $description: null as HTMLElement | null,
      descriptionEl: null as HTMLElement | null,
    }),

    computed: {
      goods(): TGoods {
        return this.elem.attributes;
      },

      title(): string | null {
        return this.goods.title?.trim() || null;
      },

      price(): string | null {
        return this.goods.price?.toString().trim() || null;
      },

      goodsLink(): string {
        return this.goods.button_link || this.goods.link || '';
      },

      goodsContent(): TContent[] {
        return this.elem.children;
      },

      isGoodsButton(): boolean {
        return Boolean(this.goods.button_text?.trim() && this.goodsLink);
      },

      descriptionContentClass(): Record<string, boolean> | null {
        return {
          'goods-item__description-content--collapse': this.isDescriptionCollapsed,
        };
      },

      descriptionContentStyle(): Record<string, string> | null {
        return isMobile
          ? {
              height: this.isDescriptionCollapsed ? `${this.descriptionHeightMobile}px` : 'auto',
            }
          : null;
      },

      image(): TImage | null {
        return this.goods.image;
      },

      sizes(): TImageSize[] {
        return this.image?.sizes || [];
      },

      fullSizeUrl(): string {
        return this.image?.origin || '';
      },

      currentSlug(): string {
        return getSlugFromUrl(this.$route.fullPath);
      },
    },

    mounted() {
      const descriptionContent: HTMLElement = this.$refs.descriptionContent as HTMLElement;
      const description: HTMLElement = this.$refs.description as HTMLElement;

      this.descriptionEl = isMobile ? descriptionContent : description;

      if (isMobile) {
        this.getDescriptionCollapsedMobile();
      }
    },

    methods: {
      onClickMore(): void {
        this.expand();
        this.sendAnalytics('Развернуть');
      },

      onClickBuy(): void {
        this.sendAnalytics('Купить');
      },

      // Функция для аналитики клика на товар
      sendAnalytics(_eventAction: string): void {},

      expand() {
        this.styleContent = {
          height: 'auto',
        };

        this.isDescriptionCollapsed = false;
      },

      async getDescriptionCollapsedMobile(): Promise<void> {
        if (!this.descriptionEl) {
          return;
        }

        this.isDescriptionCollapsed = true;
        await this.$nextTick();

        this.isDescriptionCollapsed = this.isCollapsedContent(this.descriptionEl);
      },

      async getDescriptionCollapsed(): Promise<void> {
        if (!this.descriptionEl) {
          return;
        }

        if (isMobile) {
          return;
        }

        this.isDescriptionCollapsed = true;
        await this.$nextTick();

        this.isDescriptionCollapsed = this.isCollapsedContent(this.descriptionEl);
      },

      isCollapsedContent(element: HTMLElement) {
        const { clientHeight, scrollHeight } = element as HTMLElement;

        return clientHeight < scrollHeight;
      },

      onImgLoad() {
        if (isMobile) {
          return;
        }

        // @ts-expect-error ref typing is not inferred in options API
        const image = this.$refs.image.$el as HTMLElement;
        const content = this.$refs.content as HTMLElement;

        this.styleContent = {
          height: content.offsetHeight > image.offsetHeight ? `${image.offsetHeight}px` : 'auto',
        };

        this.getDescriptionCollapsed();
      },
    },
  });
</script>

<style lang="scss" scoped>
  @use '~/scss/article' as *;

  .goods-item {
    display: grid;
    grid-template-areas:
      'image'
      'content';
    column-gap: 24px;
    padding: 16px 0;
    margin: 0;

    @include tablet {
      grid-template-areas: 'image content';
      grid-template-columns: 224px 1fr;
      row-gap: 24px;
    }

    & + & {
      margin-top: -24px !important;
    }

    h2 + &,
    h3 + &,
    h4 + & {
      margin-top: -4px !important;
    }

    &__image-container {
      width: 224px;
      margin: 0 auto;
      grid-area: image;
      margin-bottom: 16px;

      @include tablet {
        margin-bottom: 0;
        width: auto;
      }
    }

    &__content {
      grid-area: content;
      display: flex;
      flex-direction: column;
      justify-content: center;
    }

    &__picture {
      display: block;
      height: auto;
      min-height: 224px;
      max-height: 336px;
      width: 100%;
      border: 1px solid #e7e7e7;
      border-radius: 60px;
      overflow: hidden;
    }

    &__title {
      font-family: $text-font--1;
      @include normalText;
      font-weight: 500;
      margin-bottom: 8px;
      text-align: center;
      flex-shrink: 0;

      @include tablet {
        @include multilineEllipsis(2);
        text-align: left;
      }
      @include big-tablet {
        @include mediumText;
      }
    }

    &__price {
      font-family: $text-font--1;
      @include normalText;
      font-weight: 500;
      color: $default-color--1;
      margin-bottom: 8px;
      text-align: center;

      @include tablet {
        text-align: left;
      }
    }

    &__description {
      position: relative;
      overflow: hidden;
      flex-shrink: 1;

      &-shadow {
        content: '';
        position: absolute;
        left: 0;
        bottom: 0;
        width: 100%;
        height: 20px;
        background: linear-gradient(180deg, rgba(255, 255, 255, 0) 0%, #ffffff 98.96%);
      }

      &-content :deep() {
        > {
          @include article-elems;
        }

        p,
        .list {
          @include smallText;
        }

        .list {
          margin-top: 16px !important;
          margin-bottom: 16px !important;
        }

        *:first-child {
          margin-top: 0 !important;
        }

        *:last-child {
          margin-bottom: 0;
        }
      }
    }

    &__description-collapse {
      display: inline-flex;
      align-self: flex-start;
      margin-top: 8px;
      color: #777474;

      @include hover {
        color: #000000;
      }
    }

    &__button {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      @include normalText;
      font-family: $text-font--1;
      font-weight: 500;
      color: $default-color--1;
      background-color: transparent;
      border: 1px solid $default-color--1;
      min-width: 120px;
      border-radius: 24px;
      align-self: center;
      margin-top: 24px;
      padding: 7px 24px;
      text-align: center;

      @include tablet {
        align-self: flex-start;
      }

      @include hover {
        color: #fff;
        background-color: $default-color--1;
      }
    }
  }
  // обработка случая, когда есть только цена и кнопка
  .goods-item__price + .goods-item__button {
    margin-top: 16px;
  }
  // обработка случая, когда есть только тайтл и кнопка
  .goods-item__title + .goods-item__button {
    margin-top: 16px;
  }
</style>
